import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useTracker, type TrackingEvent } from '@archipro-website/tracker';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { TileCarousel } from '~/ui-rocco/pages/detail-shared';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';

/**
 * P9 — featured / related projects carousel (more work by the same
 * professional), mapped into the shared {@link TileCarousel} (dark theme). The
 * "more in this category" grid is a separate section.
 *
 * Like the product page's supplier carousel, this drives the Magic Island's
 * pagination + auto-scroll and re-surfaces a "View more projects" CTA under the
 * carousel. The About-the-Professional block above registers no island, so the
 * pagination takes over as soon as the projects are in view.
 */
export const RelatedProjectsSection = () => {
    const { project, featuredProjects } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const professional = project.Professional;
    const projects = featuredProjects?.FeaturedProjects?.projects ?? [];

    // Mobile folds this carousel into the About-the-Professional block (matching
    // the product PDP's About-the-Supplier layout), so the standalone section is
    // desktop-only here.
    if (mobile) return null;

    const tiles: DetailTile[] = projects.map((project) => ({
        id: project.id,
        link: project.link,
        title: project.title,
        images: project.images,
        subtitle: project.professional?.name,
        professional: {
            id: project.professional?.id,
            name: project.professional?.name,
        },
        professionalLink: project.professional?.link,
        pinType: 'ProjectPin',
    }));

    return (
        <TileCarousel
            theme="dark"
            // No eyebrow / hairline — the section sits directly under the
            // About-the-Supplier block. `eyebrow` is kept only for the
            // pagination aria-label.
            hideHeader
            eyebrow="Featured Projects"
            tiles={tiles}
            // Shared work-by-entity peek treatment on mobile (two tiles + a peek,
            // 16px inset). Desktop keeps the 3-up wide grid below.
            peek
            // Desktop drives the Magic Island pagination; on mobile the island
            // stays on the persistent Book-a-Meeting pill, so the carousel just
            // free-scrolls with the "View more projects" CTA below it.
            islandAutoplay={!mobile}
            hidePagination={mobile}
            islandLabel={`More from ${professional.Title}`}
            cta={{
                label: 'View more projects',
                to: `${professional.Link}/case-studies`,
            }}
            // 3-up slides with the same 1.5rem gap + wide tile aspect as the
            // Similar Projects grid below, so the rows line up. The 16px page
            // gutter lives on the embla viewport (tracking the centred 1920
            // container on wider screens): the padding caps the track's content
            // box, so basis-1/3 keeps the 1920 tile size at any width while
            // scrolling slides stay visible through the gutter to the viewport
            // edge.
            viewportClassName="md:px-[max(1rem,calc((100%_-_120rem)/2_+_1rem))]"
            itemClassName="md:basis-1/3 md:px-[0.75rem]"
            contentClassName="md:-mx-[0.75rem]"
            tileMediaClassName="md:aspect-[3/2]"
            onTileClick={(id) =>
                tracker.log('OtherProjectLikeProjectClick' as TrackingEvent, {
                    url: new URL(window.location.href),
                    data: { LibraryItemID: id },
                })
            }
        />
    );
};
